import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  ShieldCheck,
  User,
  UserCog,
  X,
} from "lucide-react";
import { toast } from "sonner";

const sb: any = supabase as any;

interface ThreadMessage {
  id: string;
  author_user_id: string;
  author_role: "reviewer" | "respondent" | "admin" | "system";
  author_name?: string | null;
  message: string;
  event_type: string;
  meta?: Record<string, any> | null;
  created_at: string;
}

interface EvidenceRequest {
  id: string;
  description: string;
  due_date: string | null;
  status: "open" | "fulfilled" | "cancelled";
  requested_by: string;
  created_at: string;
  fulfilled_at: string | null;
}

const ROLE_META: Record<string, { label: string; className: string; Icon: typeof User }> = {
  reviewer: { label: "Reviewer", className: "bg-primary/10 text-primary", Icon: ShieldCheck },
  admin: { label: "Admin", className: "bg-primary/10 text-primary", Icon: ShieldCheck },
  respondent: { label: "Respondent", className: "bg-blue-500/10 text-blue-700", Icon: User },
  system: { label: "System", className: "bg-muted text-muted-foreground", Icon: MessageSquare },
};

const REQ_STATUS_META: Record<EvidenceRequest["status"], { label: string; className: string }> = {
  open: { label: "Open", className: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  fulfilled: { label: "Fulfilled", className: "bg-green-600/15 text-green-700 border-green-600/30" },
  cancelled: { label: "Cancelled", className: "bg-muted text-muted-foreground" },
};

interface Props {
  findingId: string;
  /** Reviewer permissions: post requests, cancel requests, comment. */
  canRequest?: boolean;
  /** Respondent permission: mark evidence requests as fulfilled. */
  canFulfill?: boolean;
  /** Disable the message composer (history-only view). */
  canPost?: boolean;
  /** Optional helper text shown above the composer. */
  composerHint?: string;
  /** Composer placeholder text. */
  composerPlaceholder?: string;
}

export const FindingThread: React.FC<Props> = ({
  findingId,
  canRequest = false,
  canFulfill = false,
  canPost = true,
  composerHint,
  composerPlaceholder = "Add a message visible to both reviewer and respondent…",
}) => {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [requests, setRequests] = useState<EvidenceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [showSystem, setShowSystem] = useState(false);

  // Request composer state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqDescription, setReqDescription] = useState("");
  const [reqDueDate, setReqDueDate] = useState("");
  const [submittingReq, setSubmittingReq] = useState(false);
  const [busyReqId, setBusyReqId] = useState<string | null>(null);


  const load = useCallback(async () => {
    setLoading(true);
    const [threadRes, evRes] = await Promise.all([
      sb.rpc("cap_thread_get", { p_finding_id: findingId }),
      sb.rpc("get_finding_evidence_unified", { p_finding_id: findingId }),
    ]);
    if (threadRes.error) {
      toast.error(threadRes.error.message ?? "Failed to load conversation");
      setMessages([]);
    } else {
      setMessages((threadRes.data ?? []) as ThreadMessage[]);
    }
    if (evRes.error) {
      setRequests([]);
    } else {
      setRequests((evRes.data?.requests ?? []) as EvidenceRequest[]);
    }
    setLoading(false);
  }, [findingId]);

  useEffect(() => {
    load();
  }, [load]);

  const requestById = useMemo(() => {
    const m = new Map<string, EvidenceRequest>();
    for (const r of requests) m.set(r.id, r);
    return m;
  }, [requests]);

  const conversation = useMemo(
    () => messages.filter((m) => !isLifecycleEvent(m.event_type)),
    [messages],
  );
  const systemEvents = useMemo(
    () => messages.filter((m) => isLifecycleEvent(m.event_type)),
    [messages],
  );



  const handlePost = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    const { error } = await sb.rpc("cap_thread_post", {
      p_finding_id: findingId,
      p_message: text,
    });
    setPosting(false);
    if (error) {
      toast.error(error.message ?? "Failed to post message");
      return;
    }
    setDraft("");
    await load();
  };

  const handleSubmitRequest = async () => {
    if (!reqDescription.trim()) return;
    setSubmittingReq(true);
    const { error } = await sb.rpc("cap_request_evidence", {
      p_finding_id: findingId,
      p_description: reqDescription.trim(),
      p_due_date: reqDueDate || null,
    });
    setSubmittingReq(false);
    if (error) {
      toast.error(error.message ?? "Failed to request evidence");
      return;
    }
    setReqDescription("");
    setReqDueDate("");
    setShowRequestForm(false);
    toast.success("Evidence request sent");
    await load();
  };

  const handleFulfill = async (id: string) => {
    setBusyReqId(id);
    const { error } = await sb.rpc("cap_fulfill_evidence_request", { p_request_id: id });
    setBusyReqId(null);
    if (error) {
      toast.error(error.message ?? "Failed to mark fulfilled");
      return;
    }
    toast.success("Marked fulfilled");
    await load();
  };

  const handleCancel = async (id: string) => {
    setBusyReqId(id);
    const { error } = await sb.rpc("cap_cancel_evidence_request", { p_request_id: id });
    setBusyReqId(null);
    if (error) {
      toast.error(error.message ?? "Failed to cancel");
      return;
    }
    toast.success("Request cancelled");
    await load();
  };

  return (
    <div className="space-y-3">
      {canRequest && (
        <div className="flex justify-end">
          {!showRequestForm ? (
            <Button size="sm" variant="outline" onClick={() => setShowRequestForm(true)}>
              <FileText className="h-3.5 w-3.5 mr-1" />
              Request Evidence
            </Button>
          ) : null}
        </div>
      )}

      {canRequest && showRequestForm && (
        <div className="rounded-md border bg-amber-500/5 border-amber-500/30 p-3 space-y-2">
          <div className="text-xs font-semibold flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5 text-amber-700" />
            New Evidence Request
          </div>
          <Textarea
            value={reqDescription}
            onChange={(e) => setReqDescription(e.target.value)}
            placeholder="Describe the evidence you need (e.g., policy document, screenshot, training records)…"
            className="min-h-[60px] text-xs"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] text-muted-foreground">Due:</label>
            <Input
              type="date"
              value={reqDueDate}
              onChange={(e) => setReqDueDate(e.target.value)}
              className="h-8 text-xs w-[160px]"
            />
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowRequestForm(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitRequest}
                disabled={submittingReq || !reqDescription.trim()}
              >
                {submittingReq ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                Send Request
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading conversation…
        </div>
      ) : conversation.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No messages yet. {canRequest ? "Request evidence or start the conversation below." : "Start the conversation below."}
        </p>
      ) : (
        <ul className="space-y-2">
          {conversation.map((m) => {
            if (m.event_type === "evidence_request" && m.meta?.request_id) {
              const live = requestById.get(String(m.meta.request_id));
              return (
                <li key={m.id}>
                  <EvidenceRequestCard
                    message={m}
                    request={live}
                    canRequest={canRequest}
                    canFulfill={canFulfill}
                    busy={busyReqId === (live?.id ?? "")}
                    onFulfill={() => live && handleFulfill(live.id)}
                    onCancel={() => live && handleCancel(live.id)}
                  />
                </li>
              );
            }
            return (
              <li key={m.id}>
                <PlainMessage m={m} />
              </li>
            );
          })}
        </ul>
      )}

      {!loading && systemEvents.length > 0 && (
        <div className="rounded-md border bg-muted/20">
          <button
            type="button"
            onClick={() => setShowSystem((s) => !s)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left"
          >
            {showSystem ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold flex-1">System Activity</span>
            <Badge variant="secondary" className="text-[10px]">{systemEvents.length}</Badge>
          </button>
          {showSystem && (
            <ul className="space-y-2 px-3 pb-3">
              {systemEvents.map((m) => (
                <li key={m.id}>
                  <LifecycleEventCard m={m} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {canPost && (
        <div className="space-y-2 border-t pt-3">
          {composerHint && (
            <p className="text-[11px] text-muted-foreground">{composerHint}</p>
          )}
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={composerPlaceholder}
            className="min-h-[60px] text-sm"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground italic">
              Visible on this finding only
            </span>
            <Button size="sm" onClick={handlePost} disabled={posting || !draft.trim()}>
              {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Post Message
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};

function PlainMessage({ m }: { m: ThreadMessage }) {
  const meta = ROLE_META[m.author_role] ?? ROLE_META.system;
  const Icon = meta.Icon;
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Badge variant="outline" className={`text-[10px] gap-1 ${meta.className}`}>
          <Icon className="h-2.5 w-2.5" /> {meta.label}
        </Badge>
        {m.author_name && (
          <span className="text-[11px] text-foreground font-medium">{m.author_name}</span>
        )}
        <span className="text-[10px] text-muted-foreground">
          {new Date(m.created_at).toLocaleString()}
        </span>
        {m.event_type !== "message" && (
          <Badge variant="secondary" className="text-[10px]">
            {m.event_type.replace(/_/g, " ")}
          </Badge>
        )}
      </div>
      <p className="text-xs whitespace-pre-wrap text-foreground">{m.message}</p>
    </div>
  );
}

const LIFECYCLE_META: Record<string, { label: string; Icon: typeof User; className: string }> = {
  status_change: { label: "Status Change", Icon: Activity, className: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  progress_update: { label: "Progress Update", Icon: Activity, className: "bg-blue-500/10 text-blue-700 border-blue-500/30" },
  validation_decision: { label: "Reviewer Validation", Icon: ClipboardCheck, className: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  reviewer_comment: { label: "Reviewer Comment", Icon: ShieldCheck, className: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  evidence_fulfilled: { label: "Evidence Fulfilled", Icon: CheckCircle2, className: "bg-green-600/10 text-green-700 border-green-600/30" },
  evidence_request_cancelled: { label: "Request Cancelled", Icon: X, className: "bg-muted text-muted-foreground border-border" },
  owner_change: { label: "Owners Updated", Icon: UserCog, className: "bg-violet-500/10 text-violet-700 border-violet-500/30" },
  due_date_change: { label: "Due Date Updated", Icon: CalendarClock, className: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
};

function isLifecycleEvent(t: string) {
  return Object.prototype.hasOwnProperty.call(LIFECYCLE_META, t);
}

function LifecycleEventCard({ m }: { m: ThreadMessage }) {
  const meta = LIFECYCLE_META[m.event_type];
  const Icon = meta?.Icon ?? Activity;
  const label = meta?.label ?? m.event_type.replace(/_/g, " ");
  return (
    <div className={`rounded-md border-l-2 border bg-muted/30 px-3 py-2 ${meta?.className ?? ""}`}>
      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
        <Badge variant="outline" className={`text-[10px] gap-1 ${meta?.className ?? ""}`}>
          <Icon className="h-2.5 w-2.5" /> {label}
        </Badge>
        {m.author_name && (
          <span className="text-[11px] text-foreground font-medium">{m.author_name}</span>
        )}
        <span className="text-[10px] text-muted-foreground">
          {new Date(m.created_at).toLocaleString()}
        </span>
        <span className="text-[10px] text-muted-foreground italic ml-auto">system event</span>
      </div>
      <p className="text-xs whitespace-pre-wrap text-foreground">{m.message}</p>
    </div>
  );
}

function EvidenceRequestCard({
  message,
  request,
  canRequest,
  canFulfill,
  busy,
  onFulfill,
  onCancel,
}: {
  message: ThreadMessage;
  request?: EvidenceRequest;
  canRequest: boolean;
  canFulfill: boolean;
  busy: boolean;
  onFulfill: () => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState(true);
  const status = (request?.status ?? "open") as EvidenceRequest["status"];
  const statusMeta = REQ_STATUS_META[status];
  const description = request?.description ?? message.message.replace(/^Evidence requested:\s*/i, "");
  const dueDate = request?.due_date ?? (message.meta?.due_date as string | undefined) ?? null;
  const authorMeta = ROLE_META[message.author_role] ?? ROLE_META.reviewer;

  return (
    <div
      className={`rounded-md border px-3 py-2 ${
        status === "open"
          ? "bg-amber-500/5 border-amber-500/40"
          : status === "fulfilled"
          ? "bg-green-600/5 border-green-600/30"
          : "bg-muted/30 border-border"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        <FileText className="h-3.5 w-3.5 text-amber-700 shrink-0" />
        <span className="text-xs font-semibold truncate flex-1">Evidence Request</span>
        <Badge variant="outline" className={`text-[10px] ${statusMeta.className}`}>
          {statusMeta.label}
        </Badge>
      </button>

      {open && (
        <div className="mt-2 space-y-2 pl-5">
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
            <Badge variant="outline" className={`text-[10px] gap-1 ${authorMeta.className}`}>
              <authorMeta.Icon className="h-2.5 w-2.5" />
              {authorMeta.label}
            </Badge>
            {message.author_name && (
              <span className="text-foreground font-medium">{message.author_name}</span>
            )}
            <span>requested {new Date(message.created_at).toLocaleString()}</span>
            {dueDate && (
              <span>· Due {new Date(dueDate).toLocaleDateString()}</span>
            )}
            {request?.fulfilled_at && (
              <span>· Fulfilled {new Date(request.fulfilled_at).toLocaleString()}</span>
            )}
          </div>

          <div className="rounded border bg-card px-2.5 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
              Requested evidence
            </div>
            <p className="text-xs whitespace-pre-wrap text-foreground">{description}</p>
          </div>

          {status === "open" && (canFulfill || canRequest) && (
            <div className="flex items-center gap-2 flex-wrap">
              {canFulfill && (
                <Button size="sm" variant="outline" onClick={onFulfill} disabled={busy || !request}>
                  {busy ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  )}
                  Mark Fulfilled
                </Button>
              )}
              {canFulfill && (
                <span className="text-[10px] text-muted-foreground">
                  Upload your evidence in the Evidence tab, then mark this request fulfilled.
                </span>
              )}
              {canRequest && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={busy || !request}
                  className="ml-auto text-muted-foreground"
                >
                  <X className="h-3 w-3 mr-1" /> Cancel Request
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FindingThread;
